import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { KeyRound, LifeBuoy, LogOut, Menu, UserCircle } from 'lucide-react';
import { Avatar } from '../shared/ui/Avatar';
import { ThemeToggle } from '../shared/ui/ThemeToggle';
import { NotificationBell } from '../features/notifications';
import { AnnouncementsBell } from '../features/announcements';
import { useAuth } from '../features/auth/useAuth';
import { ChangePasswordModal } from '../features/auth';
import { usePermissions } from '../shared/hooks/usePermissions';
import { useSupportBadges, useSupportSettings } from '../features/support';
import { useToast } from '../shared/hooks/useToast';
import { useConfirmation } from '../shared/hooks/useConfirmation';
import { ArdixMark } from '../shared/brand/ArdixMark';
import { usePublicIdentity } from '../shared/brand/PublicIdentityContext';
import styles from './TopBar.module.css';

export type TopBarProps = {
  onToggleSidebar: () => void;
};

export function TopBar({ onToggleSidebar }: TopBarProps) {
  const { currentUser, userDoc, signOut } = useAuth();
  const navigate = useNavigate();
  const toast = useToast();
  const { confirm } = useConfirmation();
  const { hasAnyPermission } = usePermissions();
  const canSupport = hasAnyPermission(['OPEN_SUPPORT_TICKET', 'MANAGE_SUPPORT_TICKETS']);
  const { count: supportCount } = useSupportBadges();
  const { settings: supportSettings } = useSupportSettings();
  const { clinicName, logoUrl } = usePublicIdentity();
  const [failedUrl, setFailedUrl] = useState<string | null>(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const [changePassOpen, setChangePassOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent): void {
      if (menuOpen && menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setMenuOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [menuOpen]);

  async function handleSignOut(): Promise<void> {
    setMenuOpen(false);
    const ok = await confirm({
      title: 'Sair da conta?',
      description: 'Você precisará entrar novamente para continuar usando o Ardix.',
      confirmLabel: 'Sair',
      cancelLabel: 'Continuar conectado',
    });
    if (!ok) return;
    try {
      await signOut();
      toast.success('Sessão encerrada.');
    } catch (error) {
      console.error('[topbar] signOut error', error);
      toast.error('Não foi possível encerrar a sessão. Tente novamente.');
    }
  }

  const displayName = userDoc?.name ?? currentUser?.displayName ?? currentUser?.email ?? 'Usuário';

  return (
    <>
    <header className={styles.topbar}>
      <button
        type="button"
        className={styles['menu-toggle']}
        onClick={onToggleSidebar}
        aria-label="Abrir menu"
      >
        <Menu size={20} />
      </button>

      <Link
        to="/"
        className={styles.brand}
        aria-label={clinicName ? `Ardix · ${clinicName} — início` : 'Ardix — início'}
      >
        {/* Duas marcas, uma por breakpoint (CSS puro, sem listener): a oculta fica display:none, fora da árvore de acessibilidade. */}
        <ArdixMark tone="light" variant="full" className={styles.markFull} />
        <ArdixMark tone="light" variant="symbol" className={styles.markSymbol} />
        {clinicName && (
          <>
            <span className={styles.sep} data-testid="brand-sep" aria-hidden="true" />
            {logoUrl && failedUrl !== logoUrl && (
              <img src={logoUrl} alt="" className={styles.clinicLogo} onError={() => setFailedUrl(logoUrl)} />
            )}
            <span className={styles.clinicName}>{clinicName}</span>
          </>
        )}
      </Link>

      <div className={styles.spacer} />

      <div className={styles.actions}>
        <AnnouncementsBell buttonClassName={styles['icon-btn']} badgeClassName={styles['icon-badge']} />
        {canSupport && supportSettings.enabled ? (
          <button
            type="button"
            className={styles['icon-btn']}
            onClick={() => navigate('/support')}
            aria-label={supportCount > 0 ? `Suporte (${supportCount} não lidos)` : 'Suporte'}
            title="Suporte"
          >
            <LifeBuoy size={20} />
            {supportCount > 0 ? <span className={styles['icon-badge']}>{supportCount > 9 ? '9+' : supportCount}</span> : null}
          </button>
        ) : null}
        <NotificationBell />
        <ThemeToggle />
        <div className={styles.profile} ref={menuRef}>
          <button
            type="button"
            className={styles['profile-btn']}
            onClick={() => setMenuOpen((v) => !v)}
            aria-haspopup="menu"
            aria-expanded={menuOpen}
          >
            <Avatar name={displayName} size="sm" />
          </button>
          {menuOpen ? (
            <div className={styles['profile-menu']} role="menu">
              <div className={styles['profile-info']}>
                <div className={styles['profile-name']}>{displayName}</div>
                {currentUser?.email ? (
                  <div className={styles['profile-email']}>{currentUser.email}</div>
                ) : null}
              </div>
              <button
                type="button"
                className={styles['menu-item']}
                onClick={() => { setMenuOpen(false); setChangePassOpen(true); }}
                role="menuitem"
              >
                <KeyRound size={16} />
                Trocar senha
              </button>
              <button
                type="button"
                className={styles['menu-item']}
                onClick={() => {
                  setMenuOpen(false);
                  navigate('/profile');
                }}
                role="menuitem"
              >
                <UserCircle size={16} />
                Meu perfil
              </button>
              <button
                type="button"
                className={styles['menu-item']}
                onClick={handleSignOut}
                role="menuitem"
              >
                <LogOut size={16} />
                Sair
              </button>
            </div>
          ) : null}
        </div>
      </div>
    </header>
    <ChangePasswordModal open={changePassOpen} onClose={() => setChangePassOpen(false)} />
    </>
  );
}
